import { ChangeEvent, FocusEvent, useCallback, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';

import { IKnmPlanFind } from 'APIPackage/KnmModule/interfaces/IKnmPlanFind';
import { isStringDigitsOnly } from 'CorePackage/MainModule/validators/isStringDigitsOnly';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { knmPlanFindGet } from 'KnmPackage/MainModule/actions/knmActions';
import { KnmStatusesEnum } from 'KnmPackage/MainModule/enums/KnmStatusesEnum';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmFormRef,
  getKnmPlanFindData,
  getKnmPlanFindStatus,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';

import { composeValidators } from '../../../CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

const PLAN_ID_LENGTH = 10;

const makeKnmPlanStringValidator = (errorMsg: string) => (value: string) => {
  const valueString = value || '';

  if (valueString.length !== PLAN_ID_LENGTH || !isStringDigitsOnly(valueString)) {
    return errorMsg;
  }

  return undefined;
};

const makeKnmPlanFindValidator = (
  errorMsg: string,
  status: KnmStatusesEnum | undefined,
  getPlanGuidFormValue: () => IKnmErknmForm['planGuid'],
) => () => {
  const planGuid = getPlanGuidFormValue();

  if (status === KnmStatusesEnum.FAILED && !planGuid) {
    return errorMsg;
  }

  return undefined;
};

/**
 * Валидатор совпадения органа прокураты в плане и в паспорте
 */
const makeKnmProsecutorOrganizationValidator = (
  planFindData: IKnmPlanFind | undefined,
  getProsecutorOrganizationErknmFormValue: () => IKnmErknmForm['prosecutorOrganizationErknm'],
) => () => {
  const prosecutorOrganizationName = getProsecutorOrganizationErknmFormValue()?.fullName;

  if (planFindData?.prosecName !== prosecutorOrganizationName) {
    return 'Орган прокураты паспорта КНМ не совпадает с органом прокураты ежегодного плана КНМ';
  }

  return undefined;
};

/**
 * Валидатор совпадения КНО в плане и в паспорте
 */
const makeKnmKnoValidator = (
  planFindData: IKnmPlanFind | undefined,
  getKnoErknmFormValue: () => IKnmErknmForm['knoOrganizationErknm'],
) => () => {
  const knoDictId = getKnoErknmFormValue()?.value.dictId;
  const knoDictVersionId = getKnoErknmFormValue()?.value.dictVersionId;

  if (
    planFindData?.knmPlanData?.knoOrganizationErknm?.dictId !== knoDictId ||
    planFindData?.knmPlanData?.knoOrganizationErknm?.dictVersionId !== knoDictVersionId
  ) {
    return 'КНО паспорта КНМ не совпадает с КНО ежегодного плана КНМ';
  }

  return undefined;
};

/**
 * Валидатор совпадения вида контроля в плане и в паспорте
 */
const makeKnmSupervisionTypeValidator = (
  planFindData: IKnmPlanFind | undefined,
  getSupervisionTypeErknmFormValue: () => IKnmErknmForm['kindControl'],
) => () => {
  const knoDictId = getSupervisionTypeErknmFormValue()?.value.dictId;
  const knoDictVersionId = getSupervisionTypeErknmFormValue()?.value.dictVersionId;

  if (
    planFindData?.knmPlanData?.supervisionTypeErknm?.dictId !== knoDictId ||
    planFindData?.knmPlanData?.supervisionTypeErknm?.dictVersionId !== knoDictVersionId
  ) {
    return 'Вид контроля паспорта КНМ не совпадает с Видом контроля ежегодного плана КНМ';
  }

  return undefined;
};

interface IErknmPlanIdFieldProps {
  className?: string;
  watchOnly: boolean;
  disabled: boolean;
  planGuid: IKnmErknmForm['planGuid'];
  isSkipValidations?: boolean;
}

interface IErknmPlanIdFieldStoreProps {
  planFindData: IKnmPlanFind | undefined;
  planFindStatus: KnmStatusesEnum | undefined;
  formRef: IKnmFormRef | undefined;
  settings: IAdminSetting[] | undefined;
  created: string | null | undefined;
}

const ErknmPlanIdFieldWithoutStore = ({
  className = '',
  watchOnly,
  planGuid,
  disabled,
  formRef,
  planFindData,
  planFindStatus,
  settings,
  created,
  isSkipValidations,
}: IErknmPlanIdFieldProps & IErknmPlanIdFieldStoreProps) => {
  const dispatch = useDispatch();
  const isAutocompleted = Boolean(planGuid);
  const isDisabled = watchOnly || disabled;

  async function findPlan(value?: string) {
    const valueEdited = value?.trim();

    if (valueEdited && valueEdited.length === PLAN_ID_LENGTH) {
      if (valueEdited && isStringDigitsOnly(valueEdited)) {
        dispatchKnmPlanFindGet(Number(valueEdited));
      }
    }
  }
  const debouncedGetKnmPlanFind = debounce(findPlan, 1000);

  useEffect(() => {
    if (planFindData) {
      debouncedGetKnmPlanFind();
      formRef?.current.change('planId', `${planFindData.id}`);
      formRef?.current.change('planGuid', planFindData.guid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFindData]);

  useEffect(() => {
    if (planFindStatus === KnmStatusesEnum.FAILED) {
      const currentValue = formRef?.current.getFieldState('planId')?.value;

      if (currentValue) {
        // Двойное задание значения необходимо для активации валидации.
        formRef?.current.change('planId', `${currentValue} `);
        formRef?.current.change('planId', currentValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFindStatus]);

  const dispatchKnmPlanFindGet = useCallback((planId: number) => dispatch(knmPlanFindGet(planId)), [dispatch]);

  const handleFieldChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      debouncedGetKnmPlanFind(e.currentTarget.value);
    },
    [debouncedGetKnmPlanFind],
  );

  const handleFieldClear = useCallback(() => {
    formRef?.current.change('planGuid', undefined);
    formRef?.current.change('planId', undefined);
  }, [formRef]);

  const handleFieldBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      debouncedGetKnmPlanFind();
      const valueEdited = value?.trim();

      if (valueEdited && valueEdited.length === PLAN_ID_LENGTH) {
        if (valueEdited && isStringDigitsOnly(valueEdited)) {
          dispatchKnmPlanFindGet(Number(valueEdited));
        }
      }
    },
    [debouncedGetKnmPlanFind, dispatchKnmPlanFindGet],
  );

  const getPlanGuidFormValue = () => formRef?.current.getState().values.planGuid;

  /**
   * Функция получения органа прокуратуры в паспорте
   */
  const getProsecutorOrganizationErknmFormValue = () => formRef?.current.getState().values.prosecutorOrganizationErknm;

  /**
   * Функция получения КНО в паспорте
   */
  const getKnoErknmFormValue = () => formRef?.current.getState().values.knoOrganizationErknm;

  /**
   * Функция получения вида контроля в паспорте
   */
  const getSupervisionTypeErknmFormValue = () => formRef?.current.getState().values.kindControl;

  const isShow4125Knm = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_25_STARTS_FROM,
  );
  const isShow4125Plan = isLaterThanAdminSetting(
    planFindData?.created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_25_STARTS_FROM,
  );

  return (
    <TextInputField
      className={className}
      id="planId"
      name="planId"
      placeholder="Введите номер плана"
      clearable={!disabled && isAutocompleted}
      disabled={isDisabled}
      onChange={handleFieldChange}
      onBlur={handleFieldBlur}
      onClear={handleFieldClear}
      validate={composeValidators(
        makeRequired('Поле не может быть пустым'),
        makeKnmPlanStringValidator('Номер плана должен содержать 10 цифр'),
        makeKnmPlanFindValidator('Номер плана не найден', planFindStatus, getPlanGuidFormValue),
        isSkipValidations
          ? () => undefined
          : makeKnmProsecutorOrganizationValidator(planFindData, getProsecutorOrganizationErknmFormValue),
        isSkipValidations ? () => undefined : makeKnmKnoValidator(planFindData, getKnoErknmFormValue),
        isSkipValidations === undefined && isShow4125Knm && isShow4125Plan
          ? makeKnmSupervisionTypeValidator(planFindData, getSupervisionTypeErknmFormValue)
          : () => undefined,
      )}
    />
  );
};

const mapStateToProps = (state: IRootState): IErknmPlanIdFieldStoreProps => ({
  formRef: getKnmFormRef(state),
  planFindData: getKnmPlanFindData(state),
  planFindStatus: getKnmPlanFindStatus(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
});

export const ErknmPlanIdField = connect(mapStateToProps)(ErknmPlanIdFieldWithoutStore);
